import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoRefreshOutline, IoWalletOutline } from "react-icons/io5";
import {
  MdMenu,
  MdKeyboardArrowDown,
  MdLogout,
  MdOutlineAccountCircle,
  MdOutlineWorkspacePremium,
} from "react-icons/md";

import { profileIcon } from "../../asset";
import { getAuthUser } from "../../util/manageAuth";
import { AppDispatch, RootState } from "../../store";
import { logout } from "../../features/user/UserSlice";
import { resetWallet } from "../../features/wallet/WalletSlice";
import { useSidebar } from "../layouts/SidebarContext";

interface Props {
  children: React.ReactNode;
  className?: string;
}

export default function CustomNavbar({ children, className = "" }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { setOpen } = useSidebar();
  const { data: wallet } = useSelector((state: RootState) => state.wallet);

  const user = getAuthUser();
  const userName = user?.userName;
  const email = user?.email;
  const profilePic = user?.profilePic;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close the profile menu on outside click / Esc
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleLogout = () => {
    dispatch(resetWallet());
    dispatch(logout("Logged out successfully"));
    navigate("/login");
  };

  const balance = (wallet?.balance ?? 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <nav
      className={
        "sticky top-3 sm:top-4 z-30 w-full h-16 flex items-center gap-3 px-3 sm:px-5 rounded-2xl bg-white/75 backdrop-blur-xl backdrop-saturate-150 ring-1 ring-slate-900/[0.06] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] " +
        className
      }
    >
      {/* Mobile menu */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="lg:hidden w-10 h-10 -ml-1 flex-shrink-0 rounded-xl flex items-center justify-center text-slate-600 hover:bg-app-bg"
      >
        <MdMenu size={22} />
      </button>

      {/* Page title (passed in by each page) */}
      <div className="app-title-slot min-w-0 flex-1 flex items-center text-[17px] sm:text-[19px] font-semibold tracking-tight text-app-ink [&_*]:min-w-0 truncate">
        {children}
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        {/* Wallet balance */}
        <Link
          to="/admin/wallet"
          title="Wallet balance"
          className="group hidden sm:flex items-center gap-2.5 h-10 pl-1.5 pr-3.5 rounded-xl bg-white ring-1 ring-slate-900/[0.07] hover:ring-slate-900/[0.14] shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition"
        >
          <span className="w-7 h-7 rounded-lg bg-gradient-to-b from-emerald-400 to-emerald-600 text-white flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
            <IoWalletOutline className="text-[14px]" />
          </span>
          <span className="flex flex-col leading-none text-left">
            <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">Balance</span>
            <span className="text-[13.5px] font-semibold text-app-ink tabular-nums mt-1">₹ {balance}</span>
          </span>
        </Link>

        {/* Refresh */}
        <button
          type="button"
          onClick={handleRefresh}
          title="Refresh"
          aria-label="Refresh"
          className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 bg-white ring-1 ring-slate-900/[0.07] shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:text-app-ink hover:ring-slate-900/[0.14] transition"
        >
          <IoRefreshOutline size={18} />
        </button>

        {/* Profile menu */}
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-2.5 h-10 pl-1 pr-1 sm:pr-2 rounded-xl hover:bg-app-bg transition"
          >
            <img
              src={profilePic || profileIcon}
              alt="profile"
              className="w-8 h-8 rounded-full object-cover ring-2 ring-white shadow-sm"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = profileIcon;
              }}
            />
            <span className="hidden md:flex flex-col items-start leading-tight max-w-[160px]">
              <span className="text-[13px] font-semibold text-app-ink truncate max-w-full">{userName}</span>
              <span className="text-[11.5px] text-app-muted truncate max-w-full">{email}</span>
            </span>
            <MdKeyboardArrowDown
              className={`hidden sm:block text-slate-400 transition-transform ${menuOpen ? "rotate-180" : ""}`}
              size={18}
            />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="app-fade-up absolute right-0 top-[calc(100%+8px)] w-64 rounded-2xl bg-white border border-app-line shadow-pop p-1.5 text-left"
            >
              <div className="px-3 py-2.5">
                <p className="text-[13.5px] font-semibold text-app-ink truncate">{userName}</p>
                <p className="text-[12px] text-app-muted truncate">{email}</p>
              </div>

              {/* Balance shown here on small screens */}
              <Link
                to="/admin/wallet"
                onClick={() => setMenuOpen(false)}
                className="sm:hidden flex items-center justify-between mx-1.5 mb-1 px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 text-[13px]"
              >
                <span className="flex items-center gap-2">
                  <IoWalletOutline /> Balance
                </span>
                <span className="font-semibold tabular-nums">₹ {balance}</span>
              </Link>

              <div className="h-px bg-app-line my-1" />

              <MenuLink to="/admin/profile" icon={<MdOutlineAccountCircle />} onClick={() => setMenuOpen(false)}>
                My profile
              </MenuLink>
              <MenuLink to="/admin/wallet" icon={<IoWalletOutline />} onClick={() => setMenuOpen(false)}>
                Wallet
              </MenuLink>
              <MenuLink to="/admin/pricing" icon={<MdOutlineWorkspacePremium />} onClick={() => setMenuOpen(false)}>
                Subscription
              </MenuLink>

              <div className="h-px bg-app-line my-1" />

              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-red-600 hover:bg-red-50 transition"
              >
                <MdLogout className="text-[17px]" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function MenuLink({
  to,
  icon,
  children,
  onClick,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-slate-700 hover:bg-app-bg transition [&>svg]:text-[17px] [&>svg]:text-slate-400"
    >
      {icon}
      {children}
    </Link>
  );
}
